const MISSING_REFERENCE_VALUES = new Set(["", "null", "undefined", "n/a"]);

export const issuedOrderNumber = (value) => {
  const number = String(value ?? "").trim();
  if (MISSING_REFERENCE_VALUES.has(number.toLowerCase())) return null;
  if (/^[a-f\d]{24}$/i.test(number)) return null;
  return number;
};

export const customerOrderNumber = (booking = {}) =>
  issuedOrderNumber(booking.childOrderNumber) ||
  issuedOrderNumber(booking.teleportOrder) ||
  "Not available";

export const serviceMeta = (serviceType) => ({
  esim: { icon: "📱", title: "eSIM" },
  flights: { icon: "✈️", title: "Flights" },
  thingsToDo: { icon: "🎟️", title: "Things To Do" },
  airportLounge: { icon: "🛋️", title: "Airport Lounge" },
  hotels: { icon: "🏨", title: "Hotel" },
}[serviceType] || { icon: "📦", title: "Booking" });

export const customerStatus = (booking = {}) => {
  if (String(booking.kycStatus || "").trim()) return String(booking.kycStatus).trim();
  const status = String(booking.status || "").trim();
  if (booking.serviceType === "airportLounge" && status.toLowerCase() === "confirming") {
    return "Processing";
  }
  return status || "Status unavailable";
};

export const statusTag = (booking = {}) => {
  const text = customerStatus(booking);
  const lower = text.toLowerCase();
  if (lower.includes("cancel") || lower.includes("fail") || lower.includes("refund")) return { color: "red", text };
  if (lower.includes("await") || lower.includes("pending")) return { color: "gold", text };
  if (lower.includes("confirm") || lower.includes("complete") || lower.includes("paid") || lower.includes("issued") || lower.includes("ticketed") || lower.includes("verified")) return { color: "green", text };
  return { color: "default", text };
};

export const cleanDisplayAmount = (booking = {}) => {
  const provided = String(booking.displayAmount || "").trim().replace(/\s+/g, " ");
  if (provided) {
    const deduplicated = provided.replace(/\b([A-Z]{3})\s+\1\b/g, "$1");
    return /^USD\s+\$0(?:\.00)?$/.test(deduplicated) ? "$0.00 USD" : deduplicated;
  }
  const amount = Number(booking.amount);
  const currency = booking.currency || "USD";
  if (!Number.isFinite(amount)) return "Amount unavailable";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
};

export const formatBookingDate = (value, withTime = false, timeZone) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const options = withTime
    ? { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", ...(timeZone ? { timeZone, timeZoneName: "short" } : {}) }
    : { day: "2-digit", month: "short", year: "numeric" };
  return new Intl.DateTimeFormat("en-SG", options).format(date);
};

const firstValue = (...values) => values.find((value) => String(value ?? "").trim()) || null;

const passengerCount = (passengers) => {
  if (!passengers || typeof passengers !== "object") return null;
  const count = Object.values(passengers).reduce((total, value) => {
    const numeric = Number(value);
    return total + (Number.isFinite(numeric) ? numeric : 0);
  }, 0);
  return count > 0 ? String(count) : null;
};

const field = (label, value) => value === null || value === undefined || String(value).trim() === ""
  ? null
  : { label, value: String(value) };

export const bookingSummary = (booking = {}) => {
  if (booking.serviceType === "esim") {
    const esim = booking.esim || {};
    return {
      title: firstValue(esim.planName, esim.name, "eSIM"),
      fields: [
        field("Data", esim.displayVolume),
        field("Validity", esim.displayValidity),
        field("Purchased", formatBookingDate(booking.orderDate, true)),
      ].filter(Boolean),
    };
  }

  if (booking.serviceType === "thingsToDo") {
    const details = booking.thingsToDo || {};
    const visitDate = firstValue(details.slotDate, details.visitDate);
    const time = [details.startTime, details.endTime].filter(Boolean).join(" – ");
    return {
      title: firstValue(details.productTitle, details.packageName, details.packageOptionName, details.packageTitle, "Things To Do"),
      fields: [
        field("Package", details.packageName),
        field("Option", firstValue(details.packageOptionName, details.packageTitle)),
        field("Visit date", formatBookingDate(visitDate)),
        field("Time", time),
        field("Travellers", passengerCount(details.passengers)),
      ].filter(Boolean),
    };
  }

  if (booking.serviceType === "airportLounge") {
    const lounge = booking.airportLounge || {};
    return {
      title: firstValue(lounge.loungeName, "Airport Lounge"),
      fields: [
        field("Airport", firstValue(lounge.airport, lounge.airportIata)),
        field("Terminal", lounge.terminal),
        field("Accesses", lounge.guests),
        field("Issued", formatBookingDate(lounge.issuedAt, true)),
      ].filter(Boolean),
    };
  }

  return {
    title: firstValue(booking.title, booking.name, serviceMeta(booking.serviceType).title),
    fields: [
      field("Start date", formatBookingDate(firstValue(booking.startDate, booking.check_in_date))),
      field("End date", formatBookingDate(firstValue(booking.endDate, booking.check_out_date))),
      field("Guests", firstValue(booking.guests, booking.pax)),
    ].filter(Boolean),
  };
};

export const mergeOrders = (current = [], incoming = []) => {
  const seen = new Set();
  return [...current, ...incoming].filter((booking) => {
    const key = `${booking.serviceType || "unknown"}:${booking.orderId || booking.childOrderId || customerOrderNumber(booking)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
